/** Segment bar + overall % (from Figma ActiveTopicProgressCard). */

import type { SessionTopicPlan, TopicSegmentView } from "./types";

/** Colors for each segment status (top-bar pills + tooltips). */
export const SEGMENT_STATUS_COLORS: Record<
  TopicSegmentView["status"],
  { fill: string; track: string; border: string; label: string }
> = {
  completed: {
    fill: "#22c55e",
    track: "rgba(34, 197, 94, 0.28)",
    border: "1px solid rgba(74, 222, 128, 0.55)",
    label: "#4ade80",
  },
  skipped: {
    fill: "#f59e0b",
    track: "rgba(245, 158, 11, 0.28)",
    border: "1px solid rgba(251, 191, 36, 0.55)",
    label: "#fbbf24",
  },
  current: {
    fill: "#3b82f6",
    track: "rgba(59, 130, 246, 0.32)",
    border: "1px solid rgba(96, 165, 250, 0.75)",
    label: "#60a5fa",
  },
  pending: {
    fill: "transparent",
    track: "rgba(255, 255, 255, 0.04)",
    border: "2px solid rgba(148, 163, 184, 0.45)",
    label: "#94a3b8",
  },
};

export function getSegmentStatusColors(
  status: TopicSegmentView["status"],
) {
  return SEGMENT_STATUS_COLORS[status] ?? SEGMENT_STATUS_COLORS.pending;
}

export function getTopicStatusLabel(
  status: TopicSegmentView["status"],
): string {
  if (status === "completed") return "Completed";
  if (status === "skipped") return "Skipped";
  if (status === "current") return "Ongoing";
  return "Upcoming";
}

export function computeSessionCompletionPercent(
  topics: SessionTopicPlan[],
  currentTopic: SessionTopicPlan | undefined,
  topicProgressPercent: number,
  teachingStarted = false,
  currentSubtopicId?: string | null,
): number {
  const segments = buildTopicSegments(
    topics,
    currentTopic,
    topicProgressPercent,
    "0:00",
    teachingStarted,
    currentSubtopicId,
  );
  if (segments.length === 0) return 0;
  let sum = 0;
  for (const s of segments) {
    if (s.status === "completed" || s.status === "skipped") sum += 100;
    else if (s.status === "current") sum += s.fillPercent;
  }
  return Math.min(100, Math.round(sum / segments.length));
}

export function formatTopicDuration(minutes: number): string {
  const m = Math.floor(minutes);
  const s = Math.round((minutes - m) * 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatTopicElapsedLabel(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/** Human label matching top-bar time taken (floor minutes, no rounding). */
export function formatActualDurationDisplay(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(sec / 60);
  const seconds = sec % 60;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

export function formatSessionElapsed(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Prefer recorded actual seconds; else derive from start/end timestamps. */
export function resolveActualDurationSec(
  topic: SessionTopicPlan,
): number | null {
  let fromRange: number | null = null;
  if (topic.actualStartAt && topic.actualEndAt) {
    const start = Date.parse(topic.actualStartAt);
    const end = Date.parse(topic.actualEndAt);
    if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start) {
      fromRange = Math.floor((end - start) / 1000);
    }
  }
  const fromField =
    topic.actualDurationSec != null && topic.actualDurationSec >= 0
      ? Math.floor(topic.actualDurationSec)
      : null;
  // Take the larger value: a stale start=end (0s) must not hide a real duration.
  if (fromRange != null && fromField != null) {
    return Math.max(fromRange, fromField);
  }
  return fromRange ?? fromField;
}

/** Prefer the later of two ISO starts (drops stale earlier timestamps). */
export function laterStartIso(
  a?: string | null,
  b?: string | null,
): string | undefined {
  const aMs = a ? Date.parse(a) : NaN;
  const bMs = b ? Date.parse(b) : NaN;
  const aOk = Number.isFinite(aMs);
  const bOk = Number.isFinite(bMs);
  if (aOk && bOk) return aMs >= bMs ? (a as string) : (b as string);
  if (aOk) return a as string;
  if (bOk) return b as string;
  return undefined;
}

/** Ongoing Time taken from stored subtopic/topic start → now. */
export function resolveOngoingElapsedSec(
  topic: SessionTopicPlan,
  nowMs: number = Date.now(),
  /** Do not count time before this floor (e.g. parent topic start). */
  notBeforeIso?: string | null,
): number | null {
  if (!topic.actualStartAt || topic.actualEndAt) return null;
  const startIso = laterStartIso(topic.actualStartAt, notBeforeIso) || topic.actualStartAt;
  const start = Date.parse(startIso);
  if (!Number.isFinite(start) || start > nowMs) return null;
  return Math.max(0, Math.floor((nowMs - start) / 1000));
}

/**
 * Recover when the current open subtopic started.
 * Prefer its own start; else previous sibling end; else parent start if first.
 */
export function inferOpenSubtopicStartIso(
  topic: SessionTopicPlan | null | undefined,
  subId: string | null | undefined,
): string | undefined {
  if (!topic || !subId) return undefined;
  const subs = topic.subtopics || [];
  const idx = subs.findIndex((s) => s.id === subId);
  const open = idx >= 0 ? subs[idx] : undefined;
  if (open?.actualStartAt) return open.actualStartAt;
  for (let i = (idx >= 0 ? idx : subs.length) - 1; i >= 0; i -= 1) {
    const prev = subs[i];
    if (prev.actualEndAt) return prev.actualEndAt;
    if (
      prev.actualStartAt &&
      prev.actualDurationSec != null &&
      prev.actualDurationSec >= 0
    ) {
      const startMs = Date.parse(prev.actualStartAt);
      if (Number.isFinite(startMs)) {
        return new Date(startMs + prev.actualDurationSec * 1000).toISOString();
      }
    }
  }
  if (idx <= 0 && topic.actualStartAt) return topic.actualStartAt;
  return undefined;
}

/** Expand topics with subtopics into flat segment list. */
function flattenTopics(topics: SessionTopicPlan[]): SessionTopicPlan[] {
  const flat: SessionTopicPlan[] = [];
  for (const topic of topics) {
    if (topic.subtopics && topic.subtopics.length > 0) {
      for (const sub of topic.subtopics) {
        flat.push({
          ...sub,
          timelineStart: topic.timelineStart,
          timelineEnd: topic.timelineEnd,
          skipped: sub.skipped,
          parentTopicName: topic.name,
          isSubtopic: true,
        });
      }
    } else {
      flat.push(topic);
    }
  }
  return flat;
}

/** True when trainer has explicitly started teaching this topic (not merely joined the room). */
export function isTopicTeachingActive(
  topic: SessionTopicPlan | null | undefined,
): boolean {
  if (!topic || topic.completed || topic.skipped) return false;
  if (!topic.actualStartAt) return false;
  if (topic.actualEndAt) return false;
  return true;
}

export function buildTopicSegments(
  topics: SessionTopicPlan[],
  currentTopic?: SessionTopicPlan,
  topicProgressPercent = 0,
  currentElapsedLabel = "0:00",
  teachingStarted = false,
  currentSubtopicId?: string | null,
  nowMs: number = Date.now(),
): TopicSegmentView[] {
  const flat = flattenTopics(topics);
  const parentStartBySubId = new Map<string, string>();
  for (const topic of topics) {
    if (!topic.actualStartAt || !topic.subtopics?.length) continue;
    for (const sub of topic.subtopics) {
      parentStartBySubId.set(sub.id, topic.actualStartAt);
    }
  }

  let currentIndex = -1;
  // Linear curriculum: if a later segment is already done, earlier open ones
  // must not stay "ongoing" (out-of-order completion / lost completed flag).
  let maxDoneIndex = -1;
  for (let i = 0; i < flat.length; i++) {
    if (flat[i].completed || flat[i].skipped) maxDoneIndex = i;
  }
  const firstOpenAfterDone = flat.findIndex(
    (t, i) => i > maxDoneIndex && !t.completed && !t.skipped,
  );
  const firstOpenAny = flat.findIndex((t) => !t.completed && !t.skipped);

  if (currentSubtopicId) {
    currentIndex = flat.findIndex((t) => t.id === currentSubtopicId);
    if (currentIndex >= 0) {
      const pointed = flat[currentIndex];
      const laterDone = flat
        .slice(currentIndex + 1)
        .some((t) => t.completed || t.skipped);
      if (pointed?.completed || pointed?.skipped || laterDone) {
        currentIndex =
          firstOpenAfterDone >= 0
            ? firstOpenAfterDone
            : firstOpenAny >= 0
              ? firstOpenAny
              : -1;
      }
    }
  }
  if (currentIndex < 0 && currentTopic) {
    // Parent topic with nested subtopics → first incomplete child segment
    if (currentTopic.subtopics && currentTopic.subtopics.length > 0) {
      currentIndex =
        firstOpenAfterDone >= 0
          ? firstOpenAfterDone
          : firstOpenAny >= 0
            ? firstOpenAny
            : flat.findIndex((t) => t.id === currentTopic.subtopics![0].id);
    } else {
      currentIndex = flat.findIndex((t) => t.id === currentTopic.id);
    }
  }
  if (currentIndex < 0) {
    currentIndex =
      firstOpenAfterDone >= 0 ? firstOpenAfterDone : firstOpenAny;
  }

  return flat.map((topic, index) => {
    // skipped implies done even if completed flag lagged
    // Also heal: anything before a later completed sibling is treated as done.
    const healedDoneByOrder =
      maxDoneIndex >= 0 && index <= maxDoneIndex && index !== currentIndex;
    const isDone =
      topic.completed ||
      Boolean(topic.skipped) ||
      healedDoneByOrder ||
      (teachingStarted && currentIndex >= 0 && index < currentIndex);
    const isCurrent =
      teachingStarted &&
      index === currentIndex &&
      !topic.completed &&
      !topic.skipped &&
      !healedDoneByOrder;

    let status: TopicSegmentView["status"] = "pending";
    let fillPercent = 0;
    if (isDone && topic.skipped) {
      status = "skipped";
      fillPercent = 100;
    } else if (isDone) {
      status = "completed";
      fillPercent = 100;
    } else if (isCurrent) {
      status = "current";
      fillPercent = Math.max(0, Math.min(100, topicProgressPercent));
    }

    let timeTaken = "—";
    if (status === "current") {
      // Prefer stored start → now so ongoing Time taken matches wall clock.
      // Ignore a stale end timestamp on the live segment.
      // Clamp to parent topic start so a stale subtopic start (e.g. midnight)
      // cannot show multi-hour Time taken before teaching began.
      const parentFloor = parentStartBySubId.get(topic.id);
      const ongoingSec = resolveOngoingElapsedSec(
        topic.actualEndAt ? { ...topic, actualEndAt: undefined } : topic,
        nowMs,
        parentFloor,
      );
      timeTaken =
        ongoingSec != null
          ? formatTopicElapsedLabel(ongoingSec)
          : currentElapsedLabel;
    } else if (status === "completed" || status === "skipped") {
      const actualSec = resolveActualDurationSec(topic);
      // Never fall back to planned duration as "time taken".
      if (status === "skipped") {
        timeTaken = formatTopicElapsedLabel(actualSec ?? 0);
      } else if (actualSec != null) {
        timeTaken = formatTopicElapsedLabel(actualSec);
      } else {
        timeTaken = "—";
      }
    }

    return {
      id: topic.id,
      name: topic.name,
      status,
      fillPercent,
      timeTaken,
      expectedTime: formatTopicDuration(topic.durationMin),
      parentTopicName: topic.parentTopicName,
    };
  });
}
